import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  postComment,
  readReviewerIdentity,
  writeReviewerIdentity,
} from "./mockupApi";

const isAclymateEmail = (email) => /@aclymate\.com$/i.test(email.trim());

const SnipCommentModal = ({
  open,
  onClose,
  onSubmitted,
  thumbnailDataUrl,
  contextPayload,
}) => {
  const initialIdentity = readReviewerIdentity();
  const [name, setName] = useState(initialIdentity.name);
  const [email, setEmail] = useState(initialIdentity.email);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit =
    Boolean(comment.trim()) &&
    Boolean(name.trim()) &&
    isAclymateEmail(email) &&
    !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await postComment({
        ...contextPayload,
        comment: comment.trim(),
        reviewerName: name.trim(),
        reviewerEmail: email.trim(),
        screenshotDataUrl: thumbnailDataUrl,
      });
      writeReviewerIdentity({ name: name.trim(), email: email.trim() });
      onSubmitted(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Leave a comment</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {thumbnailDataUrl && (
            <Box
              component="img"
              src={thumbnailDataUrl}
              alt="Selected region preview"
              sx={{
                width: "100%",
                maxHeight: 280,
                objectFit: "contain",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            />
          )}
          <Typography variant="caption" color="textSecondary">
            {contextPayload.scenarioName || contextPayload.scenarioId} →{" "}
            {contextPayload.screenTitle || contextPayload.screenRoute}
          </Typography>
          <TextField
            label="Comment"
            multiline
            rows={4}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Your name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Your @aclymate.com email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            fullWidth
            required
            error={Boolean(email) && !isAclymateEmail(email)}
            helperText={
              email && !isAclymateEmail(email)
                ? "Must be an @aclymate.com address"
                : " "
            }
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
        >
          {submitting ? "Submitting…" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SnipCommentModal;
